import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { getProfileImage } from "../../core/api";
import { getProfile, updatePassword, updateProfile } from "../../core/services/profileService";
import AppImage from "../../shared/AppImage";
import { formatAdminRole, roleTone } from "../../shared/Utils";
import { ADMIN_PERMISSIONS, getEnabledPermissionKeys, isSuperAdmin } from "../../core/adminPermissions";

const Profile = () => {
  const [profile, setProfile] = useState<any>({
    name: "",
    email: "",
    role: "admin",
  });
  const [profilePreview, setProfilePreview] = useState<string>("");
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const enabledPermissions = getEnabledPermissionKeys(profile.permissions || []);

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const stored = localStorage.getItem("adminProfile");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setProfile(parsed);
          setProfilePreview(getProfileImage(parsed));
        } catch {
          // fallback to API
        }
      }

      const res = await getProfile();
      if (res.success && res.admin) {
        setProfile(res.admin);
        setProfilePreview(getProfileImage(res.admin));
        localStorage.setItem("adminProfile", JSON.stringify(res.admin));
        window.dispatchEvent(new Event("adminProfile:updated"));
      }
    } catch (error: any) {
      Swal.fire("Error", error.message || "Failed to load profile", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  const handleProfileImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProfileFile(file);
    const reader = new FileReader();
    reader.onload = () => setProfilePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleProfileSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!profile.name || !profile.email) {
      Swal.fire("Missing Fields", "Name and email are required", "warning");
      return;
    }

    setIsSavingProfile(true);
    try {
      let payload: FormData | Record<string, string>;
      if (profileFile) {
        payload = new FormData();
        payload.append("name", profile.name);
        payload.append("email", profile.email);
        payload.append("profile", profileFile);
      } else {
        payload = {
          name: profile.name,
          email: profile.email,
        };
      }

      const result = await updateProfile(payload);
      if (result.success && result.admin) {
        setProfile(result.admin);
        setProfilePreview(getProfileImage(result.admin));
        localStorage.setItem("adminProfile", JSON.stringify(result.admin));
        window.dispatchEvent(new Event("adminProfile:updated"));
        setProfileFile(null);

        Swal.fire({
          title: "Profile Updated",
          text: "Your profile information has been saved.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire(
        "Update Failed",
        error.message || "Could not update profile",
        "error",
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (
      !passwordData.oldPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      Swal.fire(
        "Missing Fields",
        "Please fill out all password fields",
        "warning",
      );
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire(
        "Password Mismatch",
        "New password and confirm password do not match",
        "warning",
      );
      return;
    }

    if (passwordData.newPassword.length < 6) {
      Swal.fire(
        "Weak Password",
        "New password must be at least 6 characters",
        "warning",
      );
      return;
    }

    setIsSavingPassword(true);
    try {
      const result = await updatePassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      if (result.success) {
        if (result.token) {
          localStorage.setItem("adminToken", result.token);
        }
        setPasswordData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        Swal.fire({
          title: "Password Updated",
          text: "Your security credentials have been updated successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire(
        "Password Update Failed",
        error.message || "Failed to change password",
        "error",
      );
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Admin Profile…</h2>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Account</div>
          <h1 className="page-title">
            My <em>Profile</em>
          </h1>
          <div className="page-sub">
            Manage your profile, password and view your panel access.
          </div>
        </div>
      </div>

      <div className="detail-grid-2">
        <div className="table-card profile-card">
          <div className="profile-upload-wrap">
            <AppImage
              src={profilePreview}
              alt={profile.name}
              crossOrigin="anonymous"
              className="profile-avatar-img"
            />
            <label
              htmlFor="profile-upload"
              className="profile-upload-btn"
              title="Change profile image"
            >
              📷
            </label>
            <input
              id="profile-upload"
              type="file"
              accept="image/*"
              onChange={handleProfileImageChange}
              hidden
            />
          </div>

          <div>
            <div className="profile-name">{profile.name || "Admin User"}</div>
            <div className="profile-email">{profile.email}</div>
          </div>

          <div className="profile-badge-row">
            <span className={`status-badge ${roleTone(profile.role)}`}>
              {formatAdminRole(profile.role)}
            </span>
            <span className="status-badge success">Active</span>
          </div>

          <section className="profile-permissions">
            <strong>Panel Access</strong>
            {isSuperAdmin() ? <p>Full access to every admin module</p> : (
              <div className="profile-permission-list">
                {ADMIN_PERMISSIONS.filter(({ key }) => enabledPermissions.includes(key)).map(({ key, label }) => <span key={key}>{label}</span>)}
              </div>
            )}
          </section>

        </div>

        <div className="page-stack">
          <section className="table-card">
            <div className="table-head">
              <h2>Profile Details</h2>
              <p>Update your name, email and profile photo</p>
            </div>
            <form
              onSubmit={handleProfileSubmit}
              className="admin-card-body admin-form-grid"
            >
              <div className="form-field">
                <label className="modal-label">Full Name</label>
                <input
                  type="text"
                  className="admin-input"
                  value={profile.name}
                  onChange={(event) =>
                    setProfile({ ...profile, name: event.target.value })
                  }
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Email Address</label>
                <input
                  type="email"
                  className="admin-input"
                  value={profile.email}
                  onChange={(event) =>
                    setProfile({ ...profile, email: event.target.value })
                  }
                  placeholder="Enter your email"
                  required
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Admin Role (Read-only)</label>
                <input
                  type="text"
                  className="admin-input"
                  value={formatAdminRole(profile.role)}
                  disabled
                />
              </div>
              <div className="form-field admin-status-field">
                <button
                  type="submit"
                  className="btn-sm btn-primary"
                  disabled={isSavingProfile}
                >
                  {isSavingProfile ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </section>

          <section className="table-card">
            <div className="table-head">
              <h2>Change Password</h2>
              <p>Update your login credentials securely</p>
            </div>
            <form
              onSubmit={handlePasswordSubmit}
              className="admin-card-body admin-form-grid"
            >
              <div className="form-field">
                <label className="modal-label">Current Password</label>
                <input
                  type="password"
                  className="admin-input"
                  value={passwordData.oldPassword}
                  onChange={(event) =>
                    setPasswordData({
                      ...passwordData,
                      oldPassword: event.target.value,
                    })
                  }
                  placeholder="Enter current password"
                />
              </div>
              <div className="form-field">
                <label className="modal-label">New Password</label>
                <input
                  type="password"
                  className="admin-input"
                  value={passwordData.newPassword}
                  onChange={(event) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: event.target.value,
                    })
                  }
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Confirm New Password</label>
                <input
                  type="password"
                  className="admin-input"
                  value={passwordData.confirmPassword}
                  onChange={(event) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: event.target.value,
                    })
                  }
                  placeholder="Re-enter new password"
                />
              </div>
              <div className="form-field admin-status-field">
                <button
                  type="submit"
                  className="btn-sm btn-outline"
                  disabled={isSavingPassword}
                >
                  {isSavingPassword ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>

    </div>
  );
};

export default Profile;
